import {
  type ReactElement,
  type ReactNode,
  type CSSProperties,
  Children,
  isValidElement,
  useState,
  useEffect,
  useMemo,
} from "react";
import { motion, AnimatePresence } from "framer-motion";

import { BentoItem, type BentoItemProps } from "./BentoItem";
import { calculateOptimalSpans } from "./gridCalculator";
import type { TileWithPosition } from "./types";

export interface BentoProps {
  accentColor?: string;
  children?: ReactNode;
  id: string;
  title: string;
}

const getGridCols = (width: number): number => {
  if (width < 1024) return 1;
  if (width < 1440) return 2;
  return 3;
};

export const BentoRoot = ({
  id,
  accentColor = "#3F107B",
  children,
  title,
}: BentoProps) => {
  const [expandedTiles, setExpandedTiles] = useState<TileWithPosition[]>([]);
  const [gridCols, setGridCols] = useState(() => {
    if (typeof window === "undefined") return 3;
    return getGridCols(window.innerWidth);
  });

  useEffect(() => {
    const updateGridCols = () => {
      setGridCols(getGridCols(window.innerWidth));
    };

    updateGridCols();
    window.addEventListener("resize", updateGridCols);
    return () => window.removeEventListener("resize", updateGridCols);
  }, []);

  const tiles = useMemo(() => {
    if (!children) return [];

    return Children.toArray(children)
      .filter(
        (child): child is ReactElement<BentoItemProps> =>
          isValidElement(child) && child.type === BentoItem,
      )
      .map(child => ({
        baseColSpan: child.props.baseColSpan,
        baseRowSpan: child.props.baseRowSpan,
        children: child.props.children,
        enabled: child.props.enabled ?? true,
        id: child.props.id,
      }));
  }, [children]);

  const enabledTiles = useMemo(
    () => tiles.filter(tile => tile.enabled),
    [tiles],
  );

  useEffect(() => {
    const optimized = calculateOptimalSpans(enabledTiles, gridCols);
    setExpandedTiles(optimized);
  }, [enabledTiles, gridCols]);

  return (
    <div
      id={id}
      className="relative overflow-x-hidden pt-10 pb-12"
      style={{ "--accent-color": accentColor } as CSSProperties}
    >
      <div className="absolute top-1/4 left-1/4 w-[908px] h-[494px] rounded-full blur-[250px] opacity-40 bg-[var(--accent-color)]" />
      <div className="absolute bottom-1/4 right-1/4 w-[908px] h-[494px] rounded-full blur-[150px] opacity-30 bg-[var(--accent-color)]" />

      <div className="absolute inset-0 [background-image:linear-gradient(rgba(99,102,241,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.02)_1px,transparent_1px)] [background-size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]" />

      <div className="absolute top-0 inset-x-0 h-32 bg-gradient-to-b from-[#0F0F35] to-transparent pointer-events-none z-20" />
      <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-t from-[#0F0F35] to-transparent pointer-events-none z-20" />

      <div className="container mx-auto px-4 py-12 relative z-10">
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-12 text-center">
          {title}
        </h1>

        <div
          className="grid auto-rows-[144px] gap-4 grid-flow-dense mx-auto"
          style={{
            gridTemplateColumns: `repeat(${gridCols}, ${gridCols === 1 ? "100%" : "405px"})`,
            maxWidth:
              gridCols === 1
                ? "100%"
                : `${gridCols * 405 + (gridCols - 1) * 16}px`,
          }}
        >
          <AnimatePresence mode="sync">
            {expandedTiles.map((tile, index) => (
              <motion.div
                key={tile.id}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{
                  duration: 0.4,
                  delay: index * 0.05,
                  layout: { duration: 0.4 },
                  ease: [0.22, 1, 0.36, 1],
                }}
                style={{
                  gridColumn: `span ${Math.min(tile.colSpan, gridCols)}`,
                  gridRow: `span ${tile.rowSpan}`,
                }}
              >
                <div className="bg-[#0e1628]/40 backdrop-blur-md border border-white/10 hover:border-[var(--accent-color)] text-white rounded-2xl h-full transition-all duration-300">
                  {tile.children}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
